// src/lib/agent/agentBus.ts
//
// A tiny in-process pub/sub bus used by the Agent Balloon and the
// pages that consume its results (Approvals queue, Header, etc).
//
// Why not a real backend?
//   The balloon is an interactive demo surface. We want reviewers to
//   see the full agent UX (open balloon → type a command → see results
//   end-to-end) without standing up the AV-Concierge backend. The bus
//   gets us the integration seam today and a single substitution point
//   (replace this module with `fetch('/api/agent/...')`) later.
//
// Event types
//   "draft:product"  — { sku, name, brand, source, summary }
//                      raised when the agent finishes a brand search
//                      and asks "Send these to catalog queue?"
//   "ui:highlight"    — { route } navigate the user to a page with
//                      attention (e.g. /admin/approvals after sending
//                      a draft).
//
// Listeners are scoped to the page session — no persistence.

export interface ProductDraft {
  sku: string;
  name: string;
  brand: string;
  source: string; // "brand-search:<brand>"
  summary: string;
  priceEUR?: number | undefined;
  category?: string | undefined;
  image?: string | undefined;
}

export type AgentEvent =
  | { type: "draft:product"; payload: ProductDraft }
  | { type: "ui:highlight"; payload: { route: string; label: string } };

type Listener = (e: AgentEvent) => void;

const listeners = new Set<Listener>();

export function emitAgentEvent(e: AgentEvent): void {
  // Keep the bus dispatch synchronous — listeners MUST be pure and
  // fast. The balloon itself swallows heavy work into React state.
  for (const fn of listeners) {
    try {
      fn(e);
    } catch (err) {
      console.warn("agent listener threw:", err);
    }
  }
}

export function subscribeAgent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Test/debug helper — drops every listener (used by vitest only).
export function _resetAgentBus(): void {
  listeners.clear();
}
